import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import SuccessScanModal from '../components/modals/SuccessScanModal';
import FailureScanModal from '../components/modals/FailureScanModal';
import ExpiredModal from '../components/modals/ExpiredModal';
import UsernameModal from '../components/modals/UsernameModal';
import { verifyTicketStatusApi } from '../api/ticket';
import { updateTicketNamesApi } from '../api/ticketName';
import useScanRouteViewport from '../hooks/useScanRouteViewport';
import {
  DEFAULT_EXPIRED_TICKET_MESSAGE,
  DEFAULT_INVALID_MESSAGE,
  clearTicket,
  getExtendTicketUrl,
  getTicketIdFromScan,
  isTicketExpired,
  isTicketExtendable,
  pickTicket,
  saveTicket,
  splitName,
  ticketHasName,
} from '../utils/ticket';
import qrTicketIcon from '../assets/img/qr-ticket-icon.png';
import BackArrowIcon from '../components/icons/BackArrowIcon';

function InfoIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12 10v6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="7.5" r="1" fill="currentColor" />
    </svg>
  );
}

const SCAN_IDLE_MS = 20000;
const SUCCESS_NAV_MS = 1800;
const NAME_ENTRY_MS = 90000;
const NAME_SAVED_MESSAGE = 'Username updated successfully!';
const READER_ID = 'gallery-qr-reader';

function Scan() {
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const scannerRef = useRef(null);
  const startIdRef = useRef(0);
  const busyRef = useRef(false);
  const handleScanRef = useRef(async () => {});
  const navTimerRef = useRef(null);

  const [cameraError, setCameraError] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [scanPaused, setScanPaused] = useState(false);
  const [resumeKey, setResumeKey] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [successScan, setSuccessScan] = useState(false);
  const [successText, setSuccessText] = useState(null);
  const [hasScannedError, setHasScannedError] = useState(false);
  const [nameTicket, setNameTicket] = useState(null);
  const [nameValue, setNameValue] = useState('');
  const [nameErr, setNameErr] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const [expiredModal, setExpiredModal] = useState(false);
  const [expiredTicketId, setExpiredTicketId] = useState('');
  const [message, setMessage] = useState(DEFAULT_INVALID_MESSAGE);

  useScanRouteViewport(true);

  const pauseReaderVideo = useCallback(() => {
    const video = document.querySelector(`#${READER_ID} video`);
    if (!video) return;
    try {
      video.pause();
    } catch {
      /* ignore */
    }
    try {
      const stream = video.srcObject;
      if (stream && typeof stream.getTracks === 'function') {
        stream.getTracks().forEach((track) => {
          try {
            track.stop();
          } catch {
            /* ignore */
          }
        });
      }
      video.srcObject = null;
      video.removeAttribute('src');
      video.load();
    } catch {
      /* ignore */
    }
  }, []);

  const stopCamera = useCallback(async () => {
    startIdRef.current += 1;
    const scanner = scannerRef.current;
    scannerRef.current = null;
    setCameraReady(false);
    pauseReaderVideo();
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
    } catch {
      /* already stopped */
    }
    try {
      scanner.clear();
    } catch {
      /* ignore */
    }
  }, [pauseReaderVideo]);

  /** Freeze last frame (for blur) without clearing the video element. */
  const freezeCameraPreview = useCallback(async () => {
    const video = document.querySelector(`#${READER_ID} video`);
    if (video) {
      try {
        video.pause();
      } catch {
        /* ignore */
      }
      try {
        const stream = video.srcObject;
        if (stream && typeof stream.getTracks === 'function') {
          stream.getTracks().forEach((track) => {
            try {
              track.stop();
            } catch {
              /* ignore */
            }
          });
        }
      } catch {
        /* ignore */
      }
    }
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      if (scanner.isScanning) await scanner.stop();
    } catch {
      /* already stopped */
    }
  }, []);

  const startCamera = useCallback(async () => {
    await stopCamera();
    const startId = startIdRef.current;
    setCameraError(false);
    setCameraReady(false);
    setScanPaused(false);

    if (!document.getElementById(READER_ID)) return;

    const scanner = new Html5Qrcode(READER_ID, { verbose: false });
    scannerRef.current = scanner;

    const onDetected = (decodedText) => {
      if (busyRef.current) return;
      busyRef.current = true;
      handleScanRef.current(decodedText);
    };

    const config = { fps: 10, aspectRatio: 1 };
    const isStale = () =>
      startId !== startIdRef.current || scannerRef.current !== scanner;

    const startSafe = async (cameraConfig) => {
      try {
        await scanner.start(cameraConfig, config, onDetected, () => {});
      } catch (error) {
        if (error?.name === 'AbortError' || isStale()) return false;
        throw error;
      }
      return !isStale();
    };

    try {
      const started = await startSafe({ facingMode: 'environment' });
      if (!started) {
        if (isStale()) {
          try {
            if (scanner.isScanning) await scanner.stop();
            scanner.clear();
          } catch {
            /* ignore */
          }
          return;
        }
        throw new Error('retry-camera');
      }
    } catch {
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (!cameras.length) throw new Error('No camera');
        if (isStale()) {
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          return;
        }
        const started = await startSafe(cameras[0].id);
        if (!started) {
          if (isStale()) {
            try {
              if (scanner.isScanning) await scanner.stop();
              scanner.clear();
            } catch {
              /* ignore */
            }
          }
          return;
        }
      } catch (error) {
        if (error?.name === 'AbortError' || isStale()) {
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          return;
        }
        if (startId === startIdRef.current) {
          scannerRef.current = null;
          try {
            scanner.clear();
          } catch {
            /* ignore */
          }
          setCameraError(true);
        }
        return;
      }
    }

    if (isStale()) {
      pauseReaderVideo();
      try {
        if (scanner.isScanning) await scanner.stop();
        scanner.clear();
      } catch {
        /* ignore */
      }
      return;
    }

    if (startId === startIdRef.current) {
      setCameraReady(true);
    }
  }, [pauseReaderVideo, stopCamera]);

  useEffect(() => {
    // Exit-transition clone is aria-hidden — don't restart the camera mid-slide.
    const exitClone = rootRef.current?.closest('[aria-hidden="true"]');
    if (exitClone) return undefined;

    try {
      clearTicket();
    } catch {
      /* ignore */
    }
    startCamera();
    return () => {
      stopCamera();
      if (navTimerRef.current) window.clearTimeout(navTimerRef.current);
    };
  }, [startCamera, stopCamera]);

  const goHome = useCallback(() => {
    // Reverse transition back to sign-in; replace so history doesn't stack /scan.
    navigate('/login', { replace: true });
  }, [navigate]);

  const goToGallery = useCallback(() => {
    navigate('/my-gallery');
  }, [navigate]);

  const finishSuccess = useCallback(
    (ticket, successMessage) => {
      saveTicket(ticket);
      setSuccessText(successMessage || null);
      setSuccessScan(true);
      setScanPaused(true);
      stopCamera();
      if (navTimerRef.current) window.clearTimeout(navTimerRef.current);
      navTimerRef.current = window.setTimeout(() => {
        setSuccessScan(false);
        goToGallery();
      }, SUCCESS_NAV_MS);
    },
    [goToGallery, stopCamera]
  );

  const scanAgain = useCallback(() => {
    busyRef.current = false;
    setScanPaused(false);
    setHasScannedError(false);
    setExpiredModal(false);
    setSuccessScan(false);
    setNameTicket(null);
    setResumeKey((count) => count + 1);
    startCamera();
  }, [startCamera]);

  const resumeScan = useCallback(() => {
    busyRef.current = false;
    setHasScannedError(false);
    setExpiredModal(false);
    setSuccessScan(false);
    setScanPaused(false);
    setResumeKey((count) => count + 1);
    startCamera();
  }, [startCamera]);

  const scanBusy =
    isLoading || successScan || hasScannedError || expiredModal || Boolean(nameTicket);

  useEffect(() => {
    if (!cameraReady || scanPaused || scanBusy) return undefined;
    const timer = window.setTimeout(() => {
      busyRef.current = true;
      setScanPaused(true);
      setCameraReady(false);
      void freezeCameraPreview();
    }, SCAN_IDLE_MS);
    return () => window.clearTimeout(timer);
  }, [cameraReady, freezeCameraPreview, resumeKey, scanBusy, scanPaused]);

  const showFailure = useCallback((text) => {
    setMessage(text || DEFAULT_INVALID_MESSAGE);
    setHasScannedError(true);
    setScanPaused(true);
    stopCamera();
  }, [stopCamera]);

  const handleScan = useCallback(
    async (decodedText) => {
      const ticketId = getTicketIdFromScan(decodedText);
      if (!ticketId) {
        showFailure(DEFAULT_INVALID_MESSAGE);
        return;
      }

      setIsLoading(true);
      try {
        const response = await verifyTicketStatusApi({ ticketId });
        const raw = response?.data || {};
        const data =
          raw.ticket || raw.validate != null || raw.isExpired != null
            ? raw
            : raw.data && typeof raw.data === 'object'
              ? raw.data
              : raw;
        const ticket = pickTicket(data);

        if (isTicketExpired(data)) {
          const expiredText = data.message || DEFAULT_EXPIRED_TICKET_MESSAGE;
          if (isTicketExtendable(data)) {
            setExpiredTicketId(ticket?.id || ticketId);
            setMessage(expiredText);
            setExpiredModal(true);
            setScanPaused(true);
            stopCamera();
          } else {
            showFailure(expiredText);
          }
          return;
        }

        const isValid =
          Boolean(ticket?.id) &&
          (data.validate === true || (data.validate === undefined && !isTicketExpired(data)));

        if (!isValid) {
          showFailure(data.message || DEFAULT_INVALID_MESSAGE);
          return;
        }

        if (ticketHasName(ticket)) {
          finishSuccess(ticket);
        } else {
          setNameValue('');
          setNameErr('');
          setSuccessText(null);
          setSuccessScan(true);
          setScanPaused(true);
          stopCamera();
          if (navTimerRef.current) window.clearTimeout(navTimerRef.current);
          navTimerRef.current = window.setTimeout(() => {
            navTimerRef.current = null;
            setSuccessScan(false);
            setNameTicket(ticket);
          }, SUCCESS_NAV_MS);
        }
      } catch (err) {
        const errorData = err?.response?.data || {};
        const errorMessage = errorData.message || err?.message || '';
        const mentionsExpired =
          typeof errorMessage === 'string' && errorMessage.toLowerCase().includes('expired');

        if (isTicketExpired(errorData) || mentionsExpired) {
          const expiredText = errorMessage || DEFAULT_EXPIRED_TICKET_MESSAGE;
          if (isTicketExtendable(errorData)) {
            setExpiredTicketId(errorData.ticket?.id || ticketId);
            setMessage(expiredText);
            setExpiredModal(true);
            setScanPaused(true);
            stopCamera();
          } else {
            showFailure(expiredText);
          }
        } else {
          showFailure(errorMessage || DEFAULT_INVALID_MESSAGE);
        }
      } finally {
        setIsLoading(false);
      }
    },
    [finishSuccess, showFailure, stopCamera]
  );

  handleScanRef.current = handleScan;

  useEffect(() => {
    if (!nameTicket) return undefined;
    const timer = window.setTimeout(() => {
      setNameTicket(null);
      setNameValue('');
      setNameErr('');
      resumeScan();
    }, NAME_ENTRY_MS);
    return () => window.clearTimeout(timer);
  }, [nameTicket, resumeScan]);

  const handleNameSubmit = useCallback(async () => {
    if (nameSaving || !nameTicket) return;
    if (!nameValue.trim()) {
      setNameErr('Name field is required.');
      return;
    }

    const { firstName, lastName } = splitName(nameValue);
    setNameErr('');
    setNameSaving(true);
    try {
      const response = await updateTicketNamesApi(nameTicket.id, {
        first_name: firstName,
        last_name: lastName || ' ',
      });

      if (!response?.success) {
        setNameErr(
          response?.message || response?.error || 'Could not save your name. Please try again.'
        );
        return;
      }

      const updated = { ...nameTicket, first_name: firstName, last_name: lastName || ' ' };
      setNameTicket(null);
      setNameValue('');
      finishSuccess(updated, NAME_SAVED_MESSAGE);
    } finally {
      setNameSaving(false);
    }
  }, [finishSuccess, nameSaving, nameTicket, nameValue]);

  return (
    <div className="scanPage">
      {/* Outside scanRouteRoot so --s/--fh match gallery back buttons */}
      <button type="button" className="galleryPage__back scanScreen__back" onClick={goHome}>
        <BackArrowIcon />
        Back
      </button>
      <div ref={rootRef} className="scanRouteRoot">
        <section className="scanScreen" aria-label="Scan QR ticket">
          <div className="scanScreen__hint">
            <InfoIcon />
            Please make sure the QR Code is clearly visible to the camera
          </div>

          <div className={`scanScreen__camera${scanPaused ? ' is-paused' : ''}`}>
            <div id={READER_ID} className="scanScreen__reader" />
            {cameraError ? (
              <p className="scanScreen__denied">Camera permission is needed to scan a ticket.</p>
            ) : null}
            {!scanPaused && !cameraError ? (
              <span className="scanLine" aria-hidden="true" />
            ) : null}
            <div className="scanScreen__frame" aria-hidden="true">
              <span className="scanScreen__corner scanScreen__corner--tl" />
              <span className="scanScreen__corner scanScreen__corner--tr" />
              <span className="scanScreen__corner scanScreen__corner--bl" />
              <span className="scanScreen__corner scanScreen__corner--br" />
            </div>
            {scanPaused || cameraError ? (
              <>
                <div className="scanScreen__overlay" aria-hidden="true" />
                <button type="button" className="scanScreen__again" onClick={scanAgain}>
                  Scan again
                </button>
              </>
            ) : null}
          </div>

          <div className="scanScreen__card">
            <span className="scanScreen__cardIcon">
              <img src={qrTicketIcon} alt="" />
            </span>
            <div className="scanScreen__cardText">
              <p className="scanScreen__cardTitle">Show your QR ticket</p>
              <p className="scanScreen__cardSub">
                Hold your QR ticket steady in front of the camera.
              </p>
            </div>
          </div>
        </section>
      </div>

      {isLoading ? (
        <div className="scanLoader" role="status">
          Verifying ticket…
        </div>
      ) : null}

      <SuccessScanModal
        show={successScan}
        message={successText}
        onHide={() => {
          setSuccessScan(false);
          goToGallery();
        }}
      />

      <FailureScanModal show={hasScannedError} message={message} onHide={resumeScan} />

      <UsernameModal
        show={Boolean(nameTicket)}
        name={nameValue}
        setName={setNameValue}
        nameErr={nameErr}
        setNameErr={setNameErr}
        onSubmit={handleNameSubmit}
        loading={nameSaving}
        seconds={NAME_ENTRY_MS / 1000}
      />

      <ExpiredModal
        show={expiredModal}
        extendTicketUrl={getExtendTicketUrl(expiredTicketId)}
        onHide={resumeScan}
        onTimeout={resumeScan}
      />
    </div>
  );
}

export default Scan;
